import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn-auto/design-system/atoms/select';
import type { InterviewLinkDraft } from '../../services/interviewLinksApi';
import {
  DEFAULT_AUDIENCE,
  DEFAULT_LINKEDIN_DAILY_CAP,
  isFirstInterviewStage,
  LINKEDIN_CAP_OPTIONS,
  type LinkAudience,
} from '../../services/linkedinShareApi';

/**
 * The LinkedIn link's two settings on a First Interview link: who the link is for, and how many
 * LinkedIn bookings it takes a day.
 *
 * Rendered only on that stage, because it is the only one with a LinkedIn link (the proxy refuses
 * the share path everywhere else), and an option nobody can use is a question nobody can answer.
 * Both fields are written only when touched: an untouched link keeps what the proxy has stored.
 */
export function LinkedInSettings({
  draft,
  onChange,
}: {
  draft: InterviewLinkDraft;
  onChange: (next: InterviewLinkDraft) => void;
}) {
  if (!isFirstInterviewStage(draft.stageId)) return null;
  const audience = draft.audience ?? DEFAULT_AUDIENCE;
  const cap = draft.linkedinDailyCap ?? DEFAULT_LINKEDIN_DAILY_CAP;

  return (
    <section
      className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
      data-testid="linkedin-settings"
    >
      <h2 className="text-sm font-semibold text-gray-900">LinkedIn link</h2>
      <p className="mt-1 text-xs text-gray-500">
        One reusable link for InMails. Prospects pick a time, and we add them to Lever.
      </p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">Who is this link for?</span>
          <Select
            value={audience}
            onValueChange={(v) => onChange({ ...draft, audience: v as LinkAudience })}
          >
            <SelectTrigger className="w-full rounded-xl" aria-label="Who is this link for?">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="both">Inbound and LinkedIn</SelectItem>
              <SelectItem value="linkedin">LinkedIn only</SelectItem>
            </SelectContent>
          </Select>
          <span className="mt-1 block text-xs text-gray-500">
            LinkedIn-only links never appear for normal applicants.
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-gray-700">
            LinkedIn bookings per day
          </span>
          <Select
            value={String(cap)}
            onValueChange={(v) => onChange({ ...draft, linkedinDailyCap: Number(v) })}
          >
            <SelectTrigger className="w-full rounded-xl" aria-label="LinkedIn bookings per day">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LINKEDIN_CAP_OPTIONS.map((o) => (
                <SelectItem key={o} value={String(o)}>
                  {o} a day
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="mt-1 block text-xs text-gray-500">
            After that, the link says to try again tomorrow.
          </span>
        </label>
      </div>
    </section>
  );
}
